import { useMemo } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import getEventsIndexConfig from 'hb-react/shared/apps/Events/containers/Index/pages/EventsAppSharedIndex/utils/getEventsIndexConfig';
import { DetailsPage } from 'hb-react/shared/apps/Events/pages/Details';
import { ZoomPage } from 'hb-react/shared/apps/Events/pages/Zoom';
import { ZoomCompletedPage } from 'hb-react/shared/apps/Events/pages/ZoomCompleted';
import { type EventsAppProps } from 'hb-react/shared/apps/Events/startup/types';
import { ShoppingCardProvider } from 'hb-react/shared/components/ShoppingCard/ShoppingCardContext';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import { IndexContainer } from '../containers/Index';
import { GuestsProvider } from '../contexts/GuestsContext';
import {
  BillingInformationPage,
  DonationsPage,
  InitPage,
  PaymentPage,
  RedirectToStandalonePaymentPage,
  RegistrationPage,
  SuccessPage,
  TicketSelectionPage,
} from '../pages/Booking';
import { Layout } from '../pages/Booking/components/Layout';

const EventsApp = ({ config, topic }: EventsAppProps) => {
  const { flagEvPaymentPageEvents } = useFeatureFlag();

  const {
    currentUser,
    currentNetwork: { features: featuresConfig },
  } = useDefaultAppContexts();

  const topicContext = useTopicContext();

  const eventsConfig = useMemo(() => {
    if (topicContext.topicId) {
      return getEventsIndexConfig('group', {
        currentUser,
        featuresConfig,
        topicId: topicContext.topicId,
        topicContext,
        location: topic?.location,
      });
    }

    return getEventsIndexConfig(
      'network',
      {
        currentUser,
        featuresConfig,
      },
      config?.custom?.page,
    );
  }, [topicContext, currentUser, featuresConfig, config?.custom?.page]);

  return (
    <Routes>
      <Route path=":eventId/*">
        <Route path="*" element={<DetailsPage config={config} />} />
        <Route path="online" element={<ZoomPage />} />
        <Route
          path="online-completed"
          element={<ZoomCompletedPage config={eventsConfig} />}
        />
        <Route
          path="booking/*"
          element={
            <GuestsProvider>
              <ShoppingCardProvider>
                <Outlet />
              </ShoppingCardProvider>
            </GuestsProvider>
          }
        >
          <Route path=":cartId/*" element={<Layout />}>
            <Route path="ticket-selection/*" element={<TicketSelectionPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route path="registration/*" element={<RegistrationPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route path="billing/*" element={<BillingInformationPage />}>
              <Route path="donations" element={<DonationsPage />} />
            </Route>
            <Route
              path="payment"
              element={
                flagEvPaymentPageEvents ? (
                  <RedirectToStandalonePaymentPage />
                ) : (
                  <PaymentPage />
                )
              }
            />
            <Route path="success" element={<SuccessPage />} />
            <Route path="*" element={<Navigate to="ticket-selection" />} />
          </Route>
          <Route index element={<InitPage />} />
        </Route>
        <Route
          path="registration"
          element={<Navigate to="../booking" replace />}
        />
      </Route>
      <Route
        path="/"
        element={
          <IndexContainer
            config={{
              ...eventsConfig,
              networkLocation: config.networkLocation,
            }}
            topic={topic}
          />
        }
      >
        <Route
          path="p/:eventId"
          element={<IndexContainer config={eventsConfig} topic={topic} />}
        />
      </Route>
    </Routes>
  );
};

export default EventsApp;
